import { defineComponent, onMounted, ref } from 'vue'
import {
  deleteResource,
  getResourceDetail,
  getResourceList,
  getUploadTempSignature,
  saveResource
} from '../heritage/api'

const CATEGORY_STATUS_OPTIONS = [
  { value: 'enabled', label: '启用' },
  { value: 'disabled', label: '禁用' }
]

const CATEGORY_TYPE_OPTIONS = [
  { value: 'memorial', label: '纪念' },
  { value: 'hall', label: '祠堂' }
]

const EMPTY_CATEGORY = {
  id: '',
  name: '',
  code: '',
  category_type: '',
  description: '',
  icon_url: '',
  cover_url: '',
  sort_order: 0,
  status: 'enabled'
}

function unwrapPayload(res) {
  return res && res.data !== undefined ? res.data : res
}

function extractList(res) {
  const payload = unwrapPayload(res)
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.data)) return payload.data
  if (payload && Array.isArray(payload.list)) return payload.list
  if (payload && Array.isArray(payload.items)) return payload.items
  if (payload && Array.isArray(payload.records)) return payload.records
  return []
}

function extractMeta(res) {
  const payload = unwrapPayload(res)
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { total: 0, page: 1, page_size: 10, total_pages: 1 }
  }
  const total = Number(payload.total || payload.count || 0)
  const page = Number(payload.page || payload.current_page || 1)
  const pageSize = Number(payload.page_size || payload.pageSize || 10)
  const totalPages = Number(
    payload.total_pages ||
      payload.totalPages ||
      (total && pageSize ? Math.ceil(total / pageSize) : 1)
  )
  return {
    total: Number.isFinite(total) ? total : 0,
    page: Number.isFinite(page) ? page : 1,
    page_size: Number.isFinite(pageSize) ? pageSize : 10,
    total_pages: Number.isFinite(totalPages) ? totalPages : 1
  }
}

function normalizeDetail(res) {
  const payload = unwrapPayload(res)
  if (payload && payload.category && typeof payload.category === 'object') {
    return payload.category
  }
  if (payload && payload.detail && typeof payload.detail === 'object') {
    return payload.detail
  }
  return payload && typeof payload === 'object' ? payload : {}
}

function getId(row) {
  if (!row || typeof row !== 'object') return ''
  const id = row.id ?? row.category_id ?? row.categoryId
  return id == null ? '' : String(id)
}

function getStatusLabel(status) {
  const matched = CATEGORY_STATUS_OPTIONS.find((item) => item.value === status)
  return matched ? matched.label : status || '—'
}

function pickCategoryType(row) {
  if (!row || typeof row !== 'object') return ''
  return row.category_type ?? row.categoryType ?? row.type ?? ''
}

function getCategoryTypeLabel(type) {
  const matched = CATEGORY_TYPE_OPTIONS.find((item) => item.value === type)
  return matched ? matched.label : type || '—'
}

function formatDate(value) {
  if (!value) return '—'
  return String(value).replace('T', ' ').slice(0, 19)
}

function buildForm(row) {
  const form = { ...EMPTY_CATEGORY, ...row }
  form.id = form.id == null ? '' : form.id
  form.category_type = pickCategoryType(row)
  form.sort_order =
    form.sort_order == null || form.sort_order === '' ? 0 : Number(form.sort_order)
  if (!Number.isFinite(form.sort_order)) form.sort_order = 0
  form.status = form.status || 'enabled'
  return form
}

export default defineComponent({
  name: 'HeritageCategoryView',
  setup() {
    const categoryList = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const detailLoading = ref(false)
    const errorMsg = ref('')

    const currentPage = ref(1)
    const pageSize = ref(10)
    const totalPages = ref(1)
    const total = ref(0)
    const keyword = ref('')
    const filterType = ref('')

    const modalVisible = ref(false)
    const modalTitle = ref('新增分类')
    const categoryForm = ref({ ...EMPTY_CATEGORY })
    const fileInputRef = ref(null)
    const uploadingImage = ref(false)
    const uploadTargetField = ref('')

    const fetchCategories = async () => {
      loading.value = true
      errorMsg.value = ''
      try {
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        }
        const q = String(keyword.value || '').trim()
        if (q) params.q = q
        if (filterType.value) params.type = filterType.value
        const res = await getResourceList('category', params)
        categoryList.value = extractList(res)
        const meta = extractMeta(res)
        currentPage.value = meta.page
        pageSize.value = meta.page_size
        totalPages.value = Math.max(1, meta.total_pages)
        total.value = meta.total
      } catch (e) {
        console.error(e)
        categoryList.value = []
        errorMsg.value = e.message || '获取分类列表失败，请稍后重试'
      } finally {
        loading.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      fetchCategories()
    }

    let searchTimer = null
    const handleSearchInput = () => {
      if (searchTimer) clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        currentPage.value = 1
        fetchCategories()
      }, 300)
    }

    const handleSearch = () => {
      if (searchTimer) {
        clearTimeout(searchTimer)
        searchTimer = null
      }
      currentPage.value = 1
      fetchCategories()
    }

    const handleTypeFilterChange = () => {
      currentPage.value = 1
      fetchCategories()
    }

    const resetSearch = () => {
      keyword.value = ''
      filterType.value = ''
      currentPage.value = 1
      fetchCategories()
    }

    const openCreate = () => {
      modalTitle.value = '新增分类'
      categoryForm.value = { ...EMPTY_CATEGORY }
      modalVisible.value = true
    }

    const openEdit = async (row) => {
      const id = getId(row)
      if (!id) {
        alert('缺少分类 ID')
        return
      }
      modalTitle.value = `编辑分类 #${id}`
      modalVisible.value = true
      detailLoading.value = true
      try {
        const res = await getResourceDetail('category', id)
        categoryForm.value = buildForm(normalizeDetail(res))
      } catch (e) {
        console.error(e)
        alert(e.message || '获取分类详情失败')
        modalVisible.value = false
      } finally {
        detailLoading.value = false
      }
    }

    const closeModal = () => {
      if (saving.value || uploadingImage.value) return
      modalVisible.value = false
    }

    const extractPresignedUrl = (signatureResponse) => {
      const source = signatureResponse?.data || signatureResponse || {}
      const presignedUrl = source.url
      if (!presignedUrl) {
        throw new Error('上传凭证缺少 url 字段，请检查接口返回')
      }
      return presignedUrl
    }

    const uploadToCos = async (file) => {
      const signatureResponse = await getUploadTempSignature(file.name)
      const presignedUrl = extractPresignedUrl(signatureResponse)
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      })
      if (!uploadResponse.ok) {
        throw new Error(`上传 COS 失败: ${uploadResponse.status}`)
      }
      return presignedUrl.split('?')[0]
    }

    const triggerImageUpload = (field) => {
      if (uploadingImage.value) return
      uploadTargetField.value = field
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
        fileInputRef.value.click()
      }
    }

    const handleSelectImage = async (event) => {
      const file = event.target.files && event.target.files[0]
      const field = uploadTargetField.value
      if (!file || !field) return
      if (!file.type || !file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }

      uploadingImage.value = true
      try {
        const imageUrl = await uploadToCos(file)
        categoryForm.value[field] = imageUrl
        alert('图片上传成功')
      } catch (e) {
        console.error('上传分类图片失败:', e)
        alert(e.message || '上传图片失败，请稍后重试')
      } finally {
        uploadingImage.value = false
        uploadTargetField.value = ''
      }
    }

    const buildPayload = () => {
      const form = categoryForm.value
      if (!String(form.name || '').trim()) throw new Error('请填写分类名称')
      if (!String(form.code || '').trim()) throw new Error('请填写分类编码')
      if (!String(form.category_type || '').trim()) throw new Error('请选择分类类型')
      const sortOrder = Number(form.sort_order)
      if (!Number.isFinite(sortOrder)) throw new Error('排序值必须是数字')

      const payload = {
        ...form,
        name: String(form.name).trim(),
        code: String(form.code).trim(),
        category_type: String(form.category_type).trim(),
        sort_order: sortOrder,
        status: form.status || 'enabled'
      }
      if (!payload.id) delete payload.id
      return payload
    }

    const handleSave = async () => {
      let payload
      try {
        payload = buildPayload()
      } catch (e) {
        alert(e.message)
        return
      }

      saving.value = true
      try {
        await saveResource('category', payload)
        alert('保存成功')
        modalVisible.value = false
        fetchCategories()
      } catch (e) {
        console.error(e)
        alert(e.message || '保存分类失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const handleDelete = async (row) => {
      const id = getId(row)
      if (!id) {
        alert('缺少分类 ID')
        return
      }
      if (!confirm(`确定要删除分类“${row.name || row.title || id}”吗？`)) return

      loading.value = true
      try {
        await deleteResource('category', Number(id))
        alert('删除成功')
        fetchCategories()
      } catch (e) {
        console.error(e)
        alert(e.message || '删除分类失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchCategories)

    return {
      CATEGORY_STATUS_OPTIONS,
      CATEGORY_TYPE_OPTIONS,
      categoryList,
      loading,
      saving,
      detailLoading,
      errorMsg,
      currentPage,
      pageSize,
      totalPages,
      total,
      keyword,
      filterType,
      modalVisible,
      modalTitle,
      categoryForm,
      fileInputRef,
      uploadingImage,
      getId,
      getStatusLabel,
      pickCategoryType,
      getCategoryTypeLabel,
      formatDate,
      fetchCategories,
      handlePageChange,
      handleSearchInput,
      handleSearch,
      handleTypeFilterChange,
      resetSearch,
      openCreate,
      openEdit,
      closeModal,
      triggerImageUpload,
      handleSelectImage,
      handleSave,
      handleDelete
    }
  }
})
